import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeftRight, CircleAlert, CircleCheck, Landmark, ReceiptText } from 'lucide-react'
import { PortalShell } from '@/components/portal/PortalShell'
import { clientApi } from '@/lib/api-client'
import { useClientId } from '@/hooks/useClientId'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { BlockedOnboardingState } from '@/components/portal/BlockedOnboardingState'
import { ComingSoonBadge } from '@/components/portal/ComingSoonBadge'
import { CopyButton } from '@/components/portal/CopyButton'
import { cn } from '@/lib/utils'
import { caretIndex, groupThousands, rawFromInput } from '@/lib/amount-input'

type FundingTab = 'deposit' | 'withdraw' | 'transfer'

const FUNDING_TABS: FundingTab[] = ['deposit', 'withdraw', 'transfer']
const TAB_LABEL_KEYS: Record<FundingTab, string> = {
  deposit: 'fundingPage.tabDeposit',
  withdraw: 'fundingPage.tabWithdraw',
  transfer: 'fundingPage.tabTransfer',
}
/** Quick picks only fill the input; validation still runs on submit. */
const QUICK_AMOUNTS = [100, 500, 1000] as const

function parseFundingTab(raw: string | null): FundingTab {
  if (raw === 'withdraw' || raw === 'transfer') return raw
  return 'deposit'
}

/* The confirmation renders below the form; bring it into view so the request doesn't look ignored. */
function revealConfirmation(el: HTMLDivElement | null) {
  if (!el) return
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' })
  el.focus({ preventScroll: true })
}

export function FundingPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseFundingTab(searchParams.get('tab'))
  const { clientId, resolving } = useClientId()
  const { data: status, isLoading: statusLoading } = useOnboardingStatus(clientId)
  const approved = ['APPROVED_BY_WP', 'APPROVED_BY_DIRUT', 'ACTIVE'].includes(status?.status ?? '')
  const banksEnabled = approved && Boolean(clientId)

  const banks = useQuery({
    queryKey: ['company-banks'],
    queryFn: () => clientApi.companyBanks(),
    enabled: banksEnabled,
  })
  const clientBanks = useQuery({
    queryKey: ['client-banks', clientId],
    queryFn: () => clientApi.clientBanks(clientId!),
    enabled: banksEnabled,
    retry: false,
  })
  const rates = useQuery({
    queryKey: ['funding-rates'],
    queryFn: () => clientApi.fundingRates(),
    enabled: banksEnabled,
  })
  const [amount, setAmount] = useState('100')
  const [amountError, setAmountError] = useState<string | null>(null)
  const maxAmount = 1_000_000_000

  function validateAmount(): boolean {
    const value = Number(amount)
    if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
      setAmountError(t('fundingPage.amountInvalid'))
      return false
    }
    if (value > maxAmount) {
      setAmountError(t('fundingPage.amountTooLarge'))
      return false
    }
    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      setAmountError(t('fundingPage.amountMaxDecimals'))
      return false
    }
    setAmountError(null)
    return true
  }
  const [companyBankId, setCompanyBankId] = useState('')
  const [clientBankId, setClientBankId] = useState('')
  /** Snapshot of what was sent, so the confirmation does not change if the form is edited afterwards. */
  const [submitted, setSubmitted] = useState<{ kind: 'deposit' | 'withdraw'; bankId: string; amount: string } | null>(
    null,
  )
  const [rate, setRate] = useState<number | ''>('')

  function setTab(next: FundingTab) {
    setSearchParams({ tab: next }, { replace: true })
  }

  const depositMut = useMutation({
    mutationFn: () =>
      clientApi.createFundRequest(clientId!, {
        kind: 'DEPOSIT',
        amount,
        company_bank_id: companyBankId || undefined,
        exchange_rate: rate || undefined,
      }),
  })

  const withdrawMut = useMutation({
    mutationFn: () =>
      clientApi.createFundRequest(clientId!, {
        kind: 'WITHDRAW',
        amount,
        client_bank_id: clientBankId || undefined,
      }),
  })

  const activeMut = tab === 'deposit' ? depositMut : withdrawMut

  /* USD amounts use comma thousands and a dot decimal in both languages, matching the field and the validator. */
  const formatUsd = (raw: string) => {
    const value = Number(raw)
    return Number.isFinite(value)
      ? `US$ ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : raw
  }

  // The field shows "1,000,000"; state (validation and payload) keeps the raw "1000000".
  const amountRef = useRef<HTMLInputElement>(null)
  const caretAfter = useRef<number | null>(null)
  const amountDisplay = groupThousands(amount)

  useLayoutEffect(() => {
    const el = amountRef.current
    if (!el || caretAfter.current === null || document.activeElement !== el) return
    const index = caretIndex(amountDisplay, caretAfter.current)
    el.setSelectionRange(index, index)
    caretAfter.current = null
  }, [amountDisplay])

  function onAmountChange(event: ChangeEvent<HTMLInputElement>) {
    const el = event.target
    const caret = el.selectionStart ?? el.value.length
    caretAfter.current = el.value.slice(0, caret).replace(/,/g, '').length
    const pasted = (event.nativeEvent as InputEvent).inputType === 'insertFromPaste'
    setAmount(rawFromInput(el.value, pasted))
  }

  /* A typed comma is usually an Indonesian decimal comma; the field adds its own separators, so ignore it. */
  function onAmountBeforeInput(event: FormEvent<HTMLInputElement>) {
    if ((event.nativeEvent as InputEvent).data === ',') event.preventDefault()
  }

  /* Backspace or Delete next to a separator removes the digit beyond it, not just the comma. */
  function onAmountKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const el = event.currentTarget
    const start = el.selectionStart
    if (start === null || start !== el.selectionEnd) return
    if (event.key === 'Backspace' && el.value[start - 1] === ',') el.setSelectionRange(start - 1, start - 1)
    if (event.key === 'Delete' && el.value[start] === ',') el.setSelectionRange(start + 1, start + 1)
  }

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const offset = event.key === 'ArrowRight' ? 1 : -1
    const next = FUNDING_TABS[(FUNDING_TABS.indexOf(tab) + offset + FUNDING_TABS.length) % FUNDING_TABS.length]
    setTab(next)
    document.getElementById(`funding-tab-${next}`)?.focus()
  }

  const submittedDeposit = submitted?.kind === 'deposit' ? submitted : null
  const submittedWithdraw = submitted?.kind === 'withdraw' ? submitted : null
  const submittedBank = (banks.data ?? []).find((b) => b.id === submittedDeposit?.bankId)

  const amountStep = (
    <FlowStep number={2} title={t('fundingPage.amountUsd')} titleFor="funding-amount">
      <div className={cn('prod-amount', amountError && 'prod-amount--invalid')}>
        <span className="prod-amount__prefix" aria-hidden="true">
          US$
        </span>
        <input
          ref={amountRef}
          id="funding-amount"
          className="prod-amount__input"
          value={amountDisplay}
          onChange={onAmountChange}
          onBeforeInput={onAmountBeforeInput}
          onKeyDown={onAmountKeyDown}
          autoComplete="off"
          aria-invalid={amountError ? true : undefined}
          aria-describedby={amountError ? 'funding-amount-error' : undefined}
        />
      </div>
      {amountError ? (
        <p id="funding-amount-error" className="prod-error">
          {amountError}
        </p>
      ) : null}
      <div className="prod-chips" role="group" aria-label={t('fundingPage.quickAmounts')}>
        {QUICK_AMOUNTS.map((value) => (
          <button
            key={value}
            type="button"
            className="prod-chip"
            aria-pressed={amount === String(value)}
            onClick={() => setAmount(String(value))}
          >
            {groupThousands(String(value))}
          </button>
        ))}
      </div>
      {tab === 'deposit' ? (
        <div className="prod-field prod-field--block prod-funding__rate">
          <label className="prod-field__label" htmlFor="funding-rate">
            {t('fundingPage.exchangeRate')}
          </label>
          <select
            id="funding-rate"
            className="prod-select"
            value={rate}
            onChange={(e) => setRate(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">{t('fundingPage.selectRate')}</option>
            {(rates.data?.exchange_rates ?? []).map((r) => (
              <option key={r} value={r}>
                {r.toLocaleString('id-ID')}
              </option>
            ))}
          </select>
          <p className="prod-field__hint">
            {t('fundingPage.amountIdr')}:{' '}
            {rate && amount ? Math.round(Number(amount) * Number(rate)).toLocaleString('id-ID') : '-'}
          </p>
        </div>
      ) : null}
    </FlowStep>
  )

  const requestError = activeMut.error ? (
    <p className="prod-error" role="alert">
      {(activeMut.error as Error).message}
    </p>
  ) : null

  return (
    <PortalShell>
      <div className="prod-funding">
        <div className="prod-funding__header">
          <h1 className="prod-text-page-title prod-text-page-title--left">{t('fundingPage.title')}</h1>
          <Link to="/funds" className="prod-money__history">
            <ReceiptText className="size-4" aria-hidden="true" />
            {t('fundingPage.viewHistory')}
          </Link>
        </div>
        {!approved && (resolving || statusLoading) ? (
          <div className="prod-next-step prod-skeleton-card" aria-busy="true">
            <span className="sr-only">{t('common.loading')}</span>
            <span className="prod-skeleton prod-skeleton--line-sm" aria-hidden="true" />
            <span className="prod-skeleton prod-skeleton--bar" aria-hidden="true" />
            <span className="prod-skeleton prod-skeleton--title" aria-hidden="true" />
            <span className="prod-skeleton prod-skeleton--button" aria-hidden="true" />
          </div>
        ) : !approved ? (
          <BlockedOnboardingState
            checklist={status?.checklist ?? []}
            clientId={clientId}
            pendingApproval={Boolean(status?.awaiting_approval) || status?.status === 'VERIFICATION_PENDING'}
            cddsNav={{
              persyaratan_accepted: status?.persyaratan_accepted,
              cdds_submitted_at: status?.cdds_submitted_at,
              awaiting_approval: status?.awaiting_approval,
            }}
          />
        ) : (
          <>
            <div className="prod-tabs prod-funding__tabs" role="tablist" aria-label={t('fundingPage.fundingTabs')}>
              {FUNDING_TABS.map((key) => (
                <button
                  key={key}
                  id={`funding-tab-${key}`}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  aria-controls="funding-panel"
                  tabIndex={tab === key ? 0 : -1}
                  className={`prod-tab ${tab === key ? 'prod-tab--active' : ''}`}
                  onClick={() => setTab(key)}
                  onKeyDown={onTabKeyDown}
                >
                  {t(TAB_LABEL_KEYS[key])}
                  {key === 'transfer' ? <ComingSoonBadge compact /> : null}
                </button>
              ))}
            </div>

            <div id="funding-panel" role="tabpanel" aria-labelledby={`funding-tab-${tab}`} className="prod-funding__panel">
              {tab === 'transfer' ? (
                <div className="prod-empty">
                  <ArrowLeftRight className="prod-empty__icon" aria-hidden="true" />
                  <p className="prod-empty__title">{t('home.transferSoon')}</p>
                  <p className="prod-empty__text">{t('fundingPage.transferEmptyBody')}</p>
                  <button type="button" className="prod-btn prod-btn--outline" onClick={() => setTab('deposit')}>
                    {t('fundingPage.goToDeposit')}
                  </button>
                </div>
              ) : tab === 'deposit' ? (
                <>
                  <p className="prod-funding__intro">{t('fundingPage.depositHint')}</p>
                  <FlowStep number={1} title={t('fundingPage.depositBankStep')} asFieldset>
                    <p className="prod-flow__help">{t('fundingPage.depositBankHelp')}</p>
                    {banks.isLoading ? (
                      <BankSkeleton />
                    ) : banks.isError ? (
                      <InlineError text={t('fundingPage.banksError')} onRetry={() => void banks.refetch()} retryLabel={t('common.retry')} />
                    ) : (banks.data ?? []).length === 0 ? (
                      <p className="prod-flow__help prod-flow__help--note">{t('fundingPage.noCompanyBanks')}</p>
                    ) : (
                      <ul className="prod-bank-list">
                        {(banks.data ?? []).map((b) => (
                          <li key={b.id} className="prod-bank">
                            <label className="prod-bank__choice">
                              <input
                                type="radio"
                                name="company-bank"
                                className="prod-bank__radio"
                                checked={companyBankId === b.id}
                                onChange={() => setCompanyBankId(b.id)}
                              />
                              <span className="prod-bank__text">
                                <span className="prod-bank__name">{b.bank_name}</span>
                                <span className="prod-bank__number">{b.account_number}</span>
                                <span className="prod-bank__holder">
                                  {t('fundingPage.accountName', { name: b.account_name })}
                                </span>
                              </span>
                            </label>
                            <CopyButton
                              value={b.account_number}
                              label={t('fundingPage.accountNumberCopy', { bank: b.bank_name })}
                              className="prod-bank__copy"
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </FlowStep>
                  {amountStep}
                  <FlowStep number={3} title={t('fundingPage.submitStep')}>
                    <button
                      type="button"
                      className="prod-btn prod-btn--gold prod-funding__submit"
                      onClick={() => {
                        if (validateAmount()) {
                          setSubmitted({ kind: 'deposit', bankId: companyBankId, amount })
                          depositMut.mutate()
                        }
                      }}
                      disabled={!clientId || depositMut.isPending || !rate}
                    >
                      {t('fundingPage.submitDeposit')}
                    </button>
                    {!rate ? <p className="prod-field__hint">{t('fundingPage.selectRate')}</p> : null}
                    {requestError}
                  </FlowStep>
                  {depositMut.isSuccess ? (
                    <div ref={revealConfirmation} className="prod-confirm" role="status" tabIndex={-1}>
                      <CircleCheck className="prod-confirm__icon" aria-hidden="true" />
                      <div className="prod-confirm__body">
                        <p className="prod-confirm__title">{t('fundingPage.requestSent')}</p>
                        {submittedDeposit ? (
                          <p className="prod-confirm__text">
                            {t('fundingPage.requestedAmount', { amount: formatUsd(submittedDeposit.amount) })}
                          </p>
                        ) : null}
                        {submittedBank ? (
                          <>
                            <p className="prod-confirm__text">
                              {t('fundingPage.transferTo', {
                                bank: submittedBank.bank_name,
                                number: submittedBank.account_number,
                                holder: submittedBank.account_name,
                              })}
                            </p>
                            <CopyButton
                              value={submittedBank.account_number}
                              label={t('fundingPage.accountNumberCopy', { bank: submittedBank.bank_name })}
                            />
                          </>
                        ) : (
                          <p className="prod-confirm__text">{t('fundingPage.depositHint')}</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="prod-funding__intro">{t('fundingPage.withdrawHint')}</p>
                  {clientBanks.isLoading ? (
                    <BankSkeleton />
                  ) : clientBanks.isError ? (
                    <InlineError
                      text={t('fundingPage.banksError')}
                      onRetry={() => void clientBanks.refetch()}
                      retryLabel={t('common.retry')}
                    />
                  ) : (clientBanks.data ?? []).length === 0 ? (
                    <div className="prod-empty">
                      <Landmark className="prod-empty__icon" aria-hidden="true" />
                      <p className="prod-empty__text">{t('fundingPage.noClientBanks')}</p>
                      <Link to="/register/data" className="prod-btn prod-btn--outline">
                        {t('form.sections.bank')}
                      </Link>
                    </div>
                  ) : (
                    <>
                      <FlowStep number={1} title={t('fundingPage.clientBank')} asFieldset>
                        <ul className="prod-bank-list">
                          {(clientBanks.data ?? []).map((b) => (
                            <li key={b.id} className="prod-bank">
                              <label className="prod-bank__choice">
                                <input
                                  type="radio"
                                  name="client-bank"
                                  className="prod-bank__radio"
                                  checked={clientBankId === b.id}
                                  onChange={() => setClientBankId(b.id)}
                                />
                                <span className="prod-bank__text">
                                  <span className="prod-bank__name">
                                    {b.bank_name}
                                    {b.is_primary ? <span className="prod-bank__tag">{t('fundingPage.primaryBank')}</span> : null}
                                  </span>
                                  <span className="prod-bank__number">{b.account_number}</span>
                                  <span className="prod-bank__holder">
                                    {t('fundingPage.accountName', { name: b.account_holder })}
                                  </span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </FlowStep>
                      {amountStep}
                      <FlowStep number={3} title={t('fundingPage.submitStep')}>
                        <button
                          type="button"
                          className="prod-btn prod-btn--gold prod-funding__submit"
                          onClick={() => {
                            if (validateAmount()) {
                              setSubmitted({ kind: 'withdraw', bankId: clientBankId, amount })
                              withdrawMut.mutate()
                            }
                          }}
                          disabled={!clientId || !clientBankId || withdrawMut.isPending}
                        >
                          {t('fundingPage.submitWithdraw')}
                        </button>
                        {!clientBankId ? <p className="prod-field__hint">{t('fundingPage.pickBankHint')}</p> : null}
                        {requestError}
                      </FlowStep>
                      {withdrawMut.isSuccess ? (
                        <div ref={revealConfirmation} className="prod-confirm" role="status" tabIndex={-1}>
                          <CircleCheck className="prod-confirm__icon" aria-hidden="true" />
                          <div className="prod-confirm__body">
                            <p className="prod-confirm__title">{t('fundingPage.requestSent')}</p>
                            {submittedWithdraw ? (
                              <p className="prod-confirm__text">
                                {t('fundingPage.requestedAmount', { amount: formatUsd(submittedWithdraw.amount) })}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </PortalShell>
  )
}

function FlowStep({
  number,
  title,
  titleFor,
  asFieldset = false,
  children,
}: {
  number: number
  title: string
  titleFor?: string
  asFieldset?: boolean
  children: ReactNode
}) {
  const marker = (
    <span className="prod-flow__num" aria-hidden="true">
      {number}
    </span>
  )
  if (asFieldset) {
    return (
      <fieldset className="prod-flow__step">
        <legend className="prod-flow__title">
          {marker}
          {title}
        </legend>
        <div className="prod-flow__content">{children}</div>
      </fieldset>
    )
  }
  return (
    <div className="prod-flow__step">
      {titleFor ? (
        <label htmlFor={titleFor} className="prod-flow__title">
          {marker}
          {title}
        </label>
      ) : (
        <p className="prod-flow__title">
          {marker}
          {title}
        </p>
      )}
      <div className="prod-flow__content">{children}</div>
    </div>
  )
}

function BankSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="prod-bank-list" aria-busy="true">
      <span className="sr-only">{t('common.loading')}</span>
      <span className="prod-skeleton prod-skeleton--tile" aria-hidden="true" />
      <span className="prod-skeleton prod-skeleton--tile" aria-hidden="true" />
    </div>
  )
}

function InlineError({ text, onRetry, retryLabel }: { text: string; onRetry: () => void; retryLabel: string }) {
  return (
    <div className="prod-inline-error" role="alert">
      <CircleAlert className="prod-inline-error__icon" aria-hidden="true" />
      <p className="prod-inline-error__text">{text}</p>
      <button type="button" className="prod-btn prod-btn--outline prod-btn--compact" onClick={onRetry}>
        {retryLabel}
      </button>
    </div>
  )
}
